import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from '@/lib/cx';
import styles from './Button.module.css';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'plain' | 'destructive' | 'destructive-filled';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  /** Ícone à direita (ex.: chevron de menu). */
  trailingIcon?: LucideIcon;
  loading?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  /** Ocupa a largura toda (rodapés de sheet no compacto). */
  block?: boolean;
  children: ReactNode;
  ref?: Ref<HTMLButtonElement>;
  'data-testid'?: string;
}

/**
 * Botão em cápsula (SPEC 9.9). Um primário por região (9.1, regra 3).
 * Press: scale .97 com mola snappy; hover só muda cor (9.8).
 */
export function Button({
  variant,
  size = 'md',
  icon: Icon,
  trailingIcon: Trailing,
  loading = false,
  disabled = false,
  onPress,
  block = false,
  children,
  className,
  type = 'button',
  ref,
  onClick,
  ...rest
}: ButtonProps) {
  const iconSize = size === 'lg' ? 18 : 16;
  const inert = disabled || loading;
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={cx(styles.button, className)}
      data-variant={variant}
      data-size={size}
      data-block={block || undefined}
      data-loading={loading || undefined}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={inert || undefined}
      onClick={
        inert
          ? undefined
          : (e) => {
              onClick?.(e);
              onPress?.();
            }
      }
    >
      {loading ? (
        <Spinner size={iconSize} decorative />
      ) : (
        Icon && <Icon size={iconSize} strokeWidth={1.75} aria-hidden="true" className={styles.icon} />
      )}
      <span className={styles.label}>{children}</span>
      {Trailing && <Trailing size={14} strokeWidth={2} aria-hidden="true" className={styles.trailing} />}
    </button>
  );
}
